/***
 * Copyright (c) Myia 2023-2025 - All Rights Reserved
 */
/* eslint-disable max-len */
import { Construction, Copy } from 'lucide-react';
import { Link } from 'react-router';

import Toggle from '../atoms/toggle/Toggle';
import TruncatedTextWithPopover from '../atoms/TruncatedTextWithPopover';
import SuiteActions from '../suites/SuiteActions';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { toast } from '../ui/use-toast';

import { Story } from '@/commons/protocols';
import { cn } from '@/lib/utils';

type StoryCardProps = {
    projectSlug: string;
    suiteSlug: string;
    story?: Story;
    isLoading: boolean;
    href?: string;
    handleRun?: (id: number) => void;
    isSubmitting?: boolean;
    selectedStoryCard?: boolean;
    setSelectedStoryCard?: (newStatus: boolean) => void;
    handleArchive?: (id: number) => void;
    handleToggleEnable?: (id: number, enable: boolean) => void;
    fromStoryPage?: boolean;
    stories?: Story[];
    highlighted?: boolean;
};

const StoryCard = ({
    projectSlug,
    suiteSlug,
    story,
    isLoading,
    href,
    handleRun,
    isSubmitting,
    selectedStoryCard,
    setSelectedStoryCard,
    handleArchive,
    handleToggleEnable,
    fromStoryPage,
    stories = [],
    highlighted = false,
}: StoryCardProps) => {
    return (
        <div
            className={cn(
                'rounded-lg px-4 py-2 transition-all duration-200',
                highlighted && 'bg-slate-100 shadow-md',
                !highlighted && selectedStoryCard && 'bg-slate-50',
            )}
        >
            <div className="flex flex-row items-center justify-between">
                {!story?.block && (
                    <div className="mr-1">
                        <Checkbox
                            id="selectStory"
                            className="border-gray-300"
                            onClick={() => {
                                setSelectedStoryCard && setSelectedStoryCard(!selectedStoryCard);
                            }}
                            checked={!!selectedStoryCard}
                        />
                    </div>
                )}
                <div className="flex items-center w-full">
                    <div className="flex flex-row w-full justify-between items-center">
                        {isLoading || story === undefined ? (
                            <>
                                <div className="h-2.5 bg-gray-200 rounded-full w-48 mb-4" />
                                <div className="h-2.5 bg-gray-200 rounded-full w-48 mb-4" />
                                <span className="sr-only">Loading...</span>
                            </>
                        ) : (
                            <>
                                <div className="flex flex-col ml-3">
                                    <h2
                                        className={`flex flex-row items-center gap-1 text-lg ${
                                            story.archived ? 'text-muted-foreground' : 'text-gray-900'
                                        } text-center sm:text-left`}
                                    >
                                        {story.maintenance && (
                                            <div className="flex items-center justify-center bg-yellow-500 p-1 rounded mr-1">
                                                <Construction />
                                            </div>
                                        )}
                                        {story.draft && !story.maintenance && (
                                            <div className="flex items-center justify-center bg-purple-500 p-1 rounded mr-1">
                                                <Construction />
                                            </div>
                                        )}
                                        <Link
                                            to={
                                                story.archived
                                                    ? `/story/${projectSlug}/${suiteSlug}/${story.slug}/${story.id}`
                                                    : `/story/${projectSlug}/${suiteSlug}/${story.slug}/edit-live`
                                            }
                                        >
                                            <TruncatedTextWithPopover text={story.name} />
                                        </Link>
                                        <button
                                            title="Copy slug to clipboard"
                                            onClick={() => {
                                                toast({
                                                    title: 'Copied story slug to clipboard',
                                                    duration: 5000,
                                                });
                                                navigator.clipboard.writeText(story.slug);
                                            }}
                                            className="ms-auto px-2 py-1 hover:bg-purple-100 rounded"
                                        >
                                            <Copy size={14} />
                                        </button>
                                    </h2>
                                </div>
                                <div className="flex flex-row gap-4 items-center">
                                    {!story.archived && !story.block && story.enabled !== undefined && (
                                        <Toggle
                                            defaultValue={story.enabled}
                                            onClick={() => {
                                                handleToggleEnable?.(story.id, !story.enabled);
                                            }}
                                        />
                                    )}
                                    {!story.archived && !story.block && (
                                        <Button
                                            className="h-8"
                                            onClick={() => handleRun?.(story.id)}
                                            disabled={isSubmitting}
                                            aria-label={`Run story ${story.name}`}
                                        >
                                            Run
                                        </Button>
                                    )}
                                    {story.archived === false && (
                                        <SuiteActions
                                            projectSlug={projectSlug}
                                            suiteSlug={suiteSlug}
                                            storyName={story.name}
                                            storyId={story.id}
                                            storySlug={story.slug}
                                            handleArchive={() => handleArchive?.(story.id)}
                                            fromStoryPage={fromStoryPage}
                                            draft={story.draft}
                                            enabled={story.enabled}
                                            maintenance={story.maintenance}
                                            archived={story.archived}
                                            block={story.block}
                                            stories={stories}
                                        />
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                    <div />
                </div>
            </div>
        </div>
    );
};

export default StoryCard;
